import { bucketAuthenticationOutcomeDuration } from './duration-bucket';
import type { AuthenticationOutcomeEvent } from './events';

/**
 * Holds a valid SSO callback's terminal event until the Backend session settles (SC-001, SC-002;
 * T105). The token exchange alone doesn't establish the dashboard session: `fetchSession` does.
 * So boot hands the callback here, and the first session result after it decides the outcome and
 * the duration bucket, measured from boot start.
 */

/** Longest a valid callback waits for its Backend session before it counts as timed out. */
export const CALLBACK_SESSION_TIMEOUT_MS = 20_000;

/** How the Backend session settled after a valid callback. */
export type CallbackSessionResult = 'established' | 'unauthorized' | 'unavailable' | 'timeout';

interface PendingCallback {
  readonly startedAt: number;
  readonly now: () => number;
  readonly report: (event: AuthenticationOutcomeEvent) => void;
  readonly timer: ReturnType<typeof setTimeout>;
}

let pending: PendingCallback | null = null;

/** Starts waiting for the Backend session of a callback that passed token validation. */
export function awaitCallbackSession(
  startedAt: number,
  report: (event: AuthenticationOutcomeEvent) => void,
  now: () => number = () => performance.now(),
): void {
  if (pending) clearTimeout(pending.timer);
  const timer = setTimeout(() => settleCallbackSession('timeout'), CALLBACK_SESSION_TIMEOUT_MS);
  pending = { startedAt, now, report, timer };
}

/** Reports the waiting callback's terminal event once; later calls do nothing. */
export function settleCallbackSession(result: CallbackSessionResult): void {
  if (!pending) return;
  const { startedAt, now, report, timer } = pending;
  pending = null;
  clearTimeout(timer);
  const durationBucket = bucketAuthenticationOutcomeDuration(now() - startedAt);
  if (result === 'established') {
    report({ stage: 'callback', outcome: 'succeeded', reason: 'none', durationBucket });
    return;
  }
  report({ stage: 'callback', outcome: 'failed', reason: result, durationBucket });
}

export function resetCallbackSessionForTesting(): void {
  if (pending) clearTimeout(pending.timer);
  pending = null;
}
